/**
 * React hook for deriving the active org's Pro trial state from the live
 * Stripe subscription details.
 *
 * Thin wrapper over {@link useSubscription}, which fetches
 * `GET /api/billing/subscription`. The countdown works for any ``status='trialing'``
 * subscription regardless of origin (auto-granted to a first-time org creator,
 * gifted by sales via the Stripe Dashboard, or extended after the fact). Stripe
 * is the source of truth; this hook just derives UI state from the existing
 * ``GET /api/billing/subscription`` response.
 *
 * Permission gating lives at the consumer level: callers that render the trial
 * chip or card check ``organization:view_billing`` themselves.
 *
 * The pure derivation lives in ``lib/trialStatus`` so unit tests can exercise
 * it without pulling the Supabase dependency chain.
 *
 * @module lib/useTrialStatus
 */
import { useSubscription, type SubscriptionResponse } from './useSubscription';
import { deriveTrialStatus, type TrialStatus } from './trialStatus';

/** Return shape of {@link useTrialStatus}. Extends the pure derivation with a loading flag. */
export interface UseTrialStatusReturn extends TrialStatus {
  /** Loading state for the underlying subscription fetch. */
  loading: boolean;
}

/**
 * Internal helper re-export for unit tests, kept for backward compatibility
 * with existing tests that import from `useTrialStatus`. Callers should use
 * {@link useTrialStatus} in component code; direct imports of this helper
 * should prefer `lib/trialStatus` to avoid the Supabase import chain.
 *
 * @param subscription Live subscription payload, or null when the org has none.
 * @param nowSeconds Current Unix timestamp (seconds).
 */
export function _deriveTrialStatusForTests(
  subscription: SubscriptionResponse | null,
  nowSeconds: number,
): TrialStatus {
  return deriveTrialStatus(subscription, nowSeconds);
}

/**
 * Hook that returns the derived trial state for the active org. Backed by
 * {@link useSubscription}, so it refetches whenever the active org changes OR
 * ``OrgProvider.billingRefreshKey`` bumps (trial activated, extended, or the
 * post-onboarding splash is dismissed). Exposes a ``loading`` flag for
 * skeleton rendering. Errors are captured to PostHog inside `useSubscription`
 * and treated as "no trial" so the UI quietly falls back to the normal plan
 * view rather than flashing an error state.
 */
export function useTrialStatus(): UseTrialStatusReturn {
  const { loading, subscription: _subscription, planName: _planName, ...trial } = useSubscription();
  return { ...trial, loading };
}
